type OperationStatus = "pending" | "complete" | "error" | "in-progress";
export type EventType = "stream-ready" | "operation-status" | "weather-background" | "open" | "error";
export interface EventData {
  eventType: EventType;
}

export interface StreamEvent extends EventData {
  hlsStreamUrl: string;
}

export interface OperationStatusEvent extends EventData {
  description: string;
  subtask: string;
  percent: number;
}

export interface WeatherBackgroundEvent extends EventData {
  imageUrl: string;
}

type Callback = (data: EventData) => void;

export interface OperationResult {
  status: string;
  result?: any;
  error?: string;
}

class ServerSideEventConsumer {
  private static instance: ServerSideEventConsumer;
  private status: OperationStatus;
  private eventSource: EventSource | null;
  private callbacks: Map<EventType, Set<Callback>>;

  private constructor() {
    this.status = "pending";
    this.callbacks = new Map<EventType, Set<Callback>>();
    this.eventSource = null;
  }

  public static getInstance(): ServerSideEventConsumer {
    if (!ServerSideEventConsumer.instance) {
      ServerSideEventConsumer.instance = new ServerSideEventConsumer();
    }
    return ServerSideEventConsumer.instance;
  }

  public initialize(): boolean {
    if (this.eventSource) {
      return false;
    }

    const forceEmail = `${process.env.REACT_APP_USER ? JSON.parse(process.env.REACT_APP_USER)?.email : ""}`;

    const baseUrl = `${process.env.REACT_APP_API_URL}/waitForAudioStream`;
    const params = forceEmail
      ? new URLSearchParams({
          forceUser: forceEmail,
        })
      : "";

    this.eventSource = new EventSource(`${baseUrl}?${params}`, {
      withCredentials: true,
    });

    this.eventSource.onopen = () => {
      this.status = "in-progress";
    };

    this.eventSource.onmessage = this.handleMessage.bind(this);

    const eventTypes = ["stream-ready", "operation-status", "weather-background", "open"];

    eventTypes.forEach((eventType) => {
      this.registerEventListener(eventType);
    });

    this.eventSource.onerror = this.handleError.bind(this);

    return true;
  }

  public close() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.status = "pending";
  }

  private registerEventListener(eventName: string): void {
    if (!this.eventSource) {
      return;
    }
    this.eventSource.addEventListener(eventName, (event: MessageEvent) => {
      const eventObj = event.data ? JSON.parse(event.data).result?.result : {};
      this.invokeCallbacks(eventName as EventType, this.makeEventData(eventName as EventType, eventObj));
    });
  }

  private handleMessage(event: MessageEvent) {
    const data: OperationResult = JSON.parse(event.data);

    if (data.status === "complete") {
      this.status = "complete";
      if (data.result?.operation === "end") {
        this.eventSource?.close();
      }
    } else if (data.status === "error") {
      this.status = "error";
      this.eventSource?.close();
    } else {
      this.status = "error";
    }
  }

  private handleError(error: Event) {
    console.error("SSE error:", error);
    this.status = "error";
    this.eventSource?.close();
  }

  private makeEventData(eventType: EventType, data: any): EventData {
    switch (eventType) {
      case "stream-ready":
        return { eventType, hlsStreamUrl: data.urlToHls } as StreamEvent;
      case "operation-status":
        return {
          eventType,
          description: data.description,
          subtask: data.subtask,
          percent: data.percent,
        } as OperationStatusEvent;
      case "weather-background":
        return { eventType, imageUrl: data.imageUrl } as WeatherBackgroundEvent;
      default:
        return { eventType };
    }
  }

  private invokeCallbacks(eventType: EventType, eventData: EventData) {
    this.callbacks.get(eventType)?.forEach((callback) => {
      callback(eventData);
    });
  }

  public registerCallback(eventType: EventType, callback: Callback) {
    if (!this.callbacks.has(eventType)) {
      this.callbacks.set(eventType, new Set<Callback>());
    }
    if (this.callbacks.get(eventType)?.has(callback)) {
      return;
    }
    this.callbacks.get(eventType)?.add(callback);
  }

  public removeCallback(eventType: EventType, callback: Callback) {
    this.callbacks.get(eventType)?.delete(callback);
  }

  public getStatus() {
    return this.status;
  }
}

export default ServerSideEventConsumer.getInstance();
